import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ShoppingBag, ArrowLeft, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import Footer from '@/components/Footer';
import FallbackImage from '@/components/FallbackImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetail = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedColor, setSelectedColor] = useState(null);
  // quantities: { [size]: number }
  const [sizeQty, setSizeQty] = useState({});
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  useEffect(() => {
    if (product && product.colors && product.colors.length > 0) {
      setSelectedColor(product.colors[0]);
      setSizeQty({});
    }
  }, [product]);

  const fetchProduct = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const encodedId = encodeURIComponent(id);
      const response = await axios.get(`${API}/products/${encodedId}`, { headers });
      setProduct(response.data);
    } catch (error) {
      toast.error('Error al cargar producto');
    } finally {
      setLoading(false);
    }
  };

  const setQtyForSize = (size, value) => {
    const clean = Math.max(0, parseInt(value || 0, 10) || 0);
    setSizeQty(prev => ({ ...prev, [size]: clean }));
  };

  const stepQty = (size, step) => {
    setSizeQty(prev => {
      const current = parseInt(prev[size] || 0, 10) || 0;
      const next = Math.max(0, current + step);
      return { ...prev, [size]: next };
    });
  };

  const priceForSize = (sizeOption) => {
    return (user && user.approved && sizeOption.pt_price) ? sizeOption.pt_price : sizeOption.pvpr_price;
  };

  const priceWithIvaForSize = (sizeOption) => priceForSize(sizeOption) * 1.21;

  const totals = () => {
    if (!selectedColor) return { units: 0, base: 0, iva: 0, total: 0 };
    let units = 0;
    let base = 0;
    selectedColor.sizes.forEach(s => {
      const q = sizeQty[s.size] || 0;
      if (q > 0) {
        units += q;
        base += q * priceForSize(s);
      }
    });
    const total = base * 1.21;
    return { units, base, iva: total - base, total };
  };

  const addAllToCart = () => {
    if (!user) {
      toast.error('Debes iniciar sesión para añadir al carrito');
      navigate('/login');
      return;
    }
    if (!selectedColor) {
      toast.error('Selecciona un color');
      return;
    }
    const rowsToAdd = [];
    selectedColor.sizes.forEach(s => {
      const q = sizeQty[s.size] || 0;
      if (q > 0 && s.stock > 0) {
        rowsToAdd.push({ size: s, qty: q });
      }
    });
    if (rowsToAdd.length === 0) {
      toast.error('Introduce al menos una cantidad');
      return;
    }
    const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
    let addedUnits = 0;
    rowsToAdd.forEach(({ size, qty }) => {
      const uniqueVariantId = size.ean || `${product.id}-${selectedColor.color}-${size.size}`;
      const defaultDelivery = new Date();
      defaultDelivery.setDate(defaultDelivery.getDate() + 7);
      const defaultDeliveryStr = defaultDelivery.toISOString().slice(0, 10);
      const cartItem = {
        variant_id: uniqueVariantId,
        reference: size.reference || size.ean || 'N/A',
        ean: size.ean,
        model_name: product.model_name,
        color: selectedColor.color,
        size: size.size,
        quantity: qty,
        price: priceForSize(size),
        list_price: size.pvpr_price,      // PVP tarifa (Q) — used to display total savings in cart
        delivery_date: defaultDeliveryStr, // +7 días por defecto (editable en el carrito)
        image: product.images[0] || '',
      };
      const idx = existingCart.findIndex(it => it.variant_id === cartItem.variant_id);
      if (idx > -1) {
        existingCart[idx].quantity += qty;
      } else {
        existingCart.push(cartItem);
      }
      addedUnits += qty;
    });
    localStorage.setItem('cart', JSON.stringify(existingCart));
    toast.success(`${addedUnits} unidad${addedUnits === 1 ? '' : 'es'} añadidas al carrito`);
    setSizeQty({});
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-neutral-600 text-lg">Cargando producto…</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-neutral-600 text-lg">Producto no encontrado</p>
      </div>
    );
  }

  const { units, base, iva, total } = totals();
  const anyStockAvailable = selectedColor && selectedColor.sizes.some(s => s.stock > 0);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="bg-black border-b border-neutral-800">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/catalog" className="flex items-center space-x-2 text-white hover:text-neutral-300 transition">
              <ArrowLeft className="w-5 h-5" />
              <span>Volver al catálogo</span>
            </Link>
            <Link to="/" className="flex items-center gap-3">
              <img src="/sudeporte-logo.png" alt="Sudeporte" className="h-9" />
              <span className="hidden sm:block h-8 w-px bg-neutral-700" />
              <img src="/macron-msh-neg-h44.png" alt="Macron Sports Hub" className="hidden sm:block h-7" />
            </Link>
          </div>
        </div>
      </header>

      {/* Product Details */}
      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8">
            {/* Images */}
            <div>
              {/* Main Image */}
              <div className="bg-gray-100 rounded-xl overflow-hidden mb-4" style={{ height: '500px' }}>
                <FallbackImage
                  images={product.images && product.images.length > 0 ? [product.images[selectedImage]] : []}
                  alt={product.model_name}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Thumbnails - Mostrar hasta 7 imágenes */}
              {product.images && product.images.length > 1 && (
                <div className="grid grid-cols-7 gap-2">
                  {product.images.slice(0, 7).map((image, index) => (
                    <button
                      key={index}
                      data-testid={`thumbnail-${index}`}
                      onClick={() => setSelectedImage(index)}
                      className={`rounded-lg overflow-hidden border-2 transition ${
                        selectedImage === index ? 'border-blue-600' : 'border-gray-200'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`${product.model_name} ${index + 1}`}
                        className="w-full h-20 object-cover"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/100?text=No+Image';
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="space-y-6">
              <div>
                <p className="text-xs uppercase tracking-widest text-neutral-500 mb-1">
                  {product.familia || 'MACRON'}{product.subfamilia ? ` · ${product.subfamilia}` : ''}
                </p>
                <h1 className="text-4xl font-black text-black leading-tight mb-2">
                  {product.model_name}
                </h1>
                <p className="text-neutral-600">{product.description}</p>
                {product.colors && product.colors[0]?.sizes?.[0]?.reference && (
                  <p className="text-neutral-500 text-sm mt-3">
                    REF: <span className="font-mono">{product.colors[0].sizes[0].reference}</span>
                  </p>
                )}
              </div>

              {/* Reference Price banner — solo visible si hay usuario logueado */}
              {user && selectedColor && selectedColor.sizes[0] && (
                <div className="bg-neutral-50 rounded-xl p-4 border border-neutral-200">
                  {user && (user.role === 'admin' || user.approved) && selectedColor.sizes[0].pt_price ? (
                    <div className="flex items-baseline gap-3">
                      <span className="text-sm text-neutral-400 line-through">
                        PVP €{selectedColor.sizes[0].pvpr_price.toFixed(2)}
                      </span>
                      <span className="text-2xl font-black text-black">
                        €{(selectedColor.sizes[0].pt_price * 1.21).toFixed(2)}
                      </span>
                      <span className="text-[10px] uppercase tracking-widest text-neutral-500">
                        IVA incl · −24% dto
                      </span>
                    </div>
                  ) : (
                    <p className="text-2xl font-black text-black">
                      €{(selectedColor.sizes[0].pvpr_price * 1.21).toFixed(2)}
                      <span className="ml-3 text-[10px] uppercase tracking-widest text-neutral-500 font-normal">Tarifa IVA incl</span>
                    </p>
                  )}
                </div>
              )}
              {!user && (
                <div className="bg-black text-white rounded-xl p-5 border border-black">
                  <p className="text-xs uppercase tracking-widest text-neutral-400">🔒 Precios ocultos</p>
                  <p className="text-lg font-black mt-1">
                    Inicia sesión como Deportes Tomás para ver los precios y hacer pedido.
                  </p>
                  <Link to="/login">
                    <Button className="mt-3 bg-white text-black hover:bg-neutral-200 font-bold">
                      Iniciar sesión
                    </Button>
                  </Link>
                </div>
              )}

              {/* Color Selection */}
              {product.colors && product.colors.length > 1 && (
                <div>
                  <h3 className="text-lg font-semibold text-black mb-3">Color</h3>
                  <div className="flex flex-wrap gap-3">
                    {product.colors.map((colorOption, index) => (
                      <button
                        key={index}
                        data-testid={`color-option-${index}`}
                        onClick={() => {
                          setSelectedColor(colorOption);
                          setSizeQty({});
                        }}
                        className={`px-4 py-2 rounded-lg border-2 transition ${
                          selectedColor?.color === colorOption.color
                            ? 'border-black bg-black text-white'
                            : 'border-neutral-200 hover:border-black'
                        }`}
                      >
                        {colorOption.color}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Bulk size + qty grid */}
              {selectedColor && (
                <div>
                  <div className="flex items-baseline justify-between mb-3">
                    <h3 className="text-lg font-semibold text-black">Tallas y cantidades</h3>
                    <span className="text-xs text-neutral-500 uppercase tracking-widest">
                      Introduce todas las cantidades del tirón
                    </span>
                  </div>

                  <div className="border border-neutral-200 rounded-xl overflow-hidden">
                    <div className="grid grid-cols-12 bg-neutral-100 px-4 py-2 text-[11px] uppercase tracking-widest text-neutral-600 font-bold">
                      <div className="col-span-3">Talla</div>
                      <div className="col-span-3">Estado</div>
                      <div className="col-span-3">Precio ud.</div>
                      <div className="col-span-3 text-right">Cantidad</div>
                    </div>
                    <div className="divide-y divide-neutral-100">
                      {selectedColor.sizes.map((s, i) => {
                        const disabled = s.stock === 0;
                        const q = sizeQty[s.size] || 0;
                        return (
                          <div key={i} className={`grid grid-cols-12 items-center px-4 py-3 ${disabled ? 'bg-neutral-50 text-neutral-400' : 'bg-white'}`}>
                            <div className="col-span-3 font-black text-lg">{s.size}</div>
                            <div className="col-span-3 text-xs uppercase tracking-widest">
                              {disabled ? 'No stock' : 'Disponible'}
                            </div>
                            <div className="col-span-3 text-sm">
                              {disabled ? '—' : (user ? `€${priceWithIvaForSize(s).toFixed(2)}` : '🔒')}
                            </div>
                            <div className="col-span-3 flex items-center justify-end gap-1">
                              <button
                                type="button"
                                data-testid={`qty-dec-${s.size}`}
                                disabled={disabled || q === 0}
                                onClick={() => stepQty(s.size, -1)}
                                className="w-8 h-8 rounded-md border border-neutral-300 hover:border-black disabled:opacity-30 disabled:cursor-not-allowed font-bold"
                              >
                                −
                              </button>
                              <input
                                type="number"
                                min="0"
                                inputMode="numeric"
                                data-testid={`qty-input-${s.size}`}
                                disabled={disabled}
                                value={q}
                                onChange={(e) => setQtyForSize(s.size, e.target.value)}
                                onFocus={(e) => e.target.select()}
                                className="w-14 h-8 border border-neutral-300 rounded-md text-center font-bold focus:outline-none focus:border-black disabled:opacity-30 disabled:cursor-not-allowed"
                              />
                              <button
                                type="button"
                                data-testid={`qty-inc-${s.size}`}
                                disabled={disabled}
                                onClick={() => stepQty(s.size, 1)}
                                className="w-8 h-8 rounded-md border border-neutral-300 hover:border-black disabled:opacity-30 disabled:cursor-not-allowed font-bold"
                              >
                                +
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Totals summary */}
              {selectedColor && (
                <div className="bg-black text-white rounded-xl p-5 flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-neutral-400">
                      Total unidades
                    </p>
                    <p className="text-3xl font-black leading-none mt-1" data-testid="pd-total-units">
                      {units}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs uppercase tracking-widest text-neutral-400">
                      Subtotal (IVA incl.)
                    </p>
                    <p className="text-3xl font-black leading-none mt-1" data-testid="pd-total-amount">
                      €{total.toFixed(2)}
                    </p>
                    {units > 0 && (
                      <p className="text-[10px] uppercase tracking-widest text-neutral-400 mt-1">
                        Base €{base.toFixed(2)} + IVA €{iva.toFixed(2)}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Add to Cart */}
              {user && user.role !== 'admin' && !user.approved ? (
                <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-4 text-center">
                  <p className="text-amber-900 font-semibold mb-2">⏳ Cuenta pendiente de aprobación</p>
                  <p className="text-sm text-amber-800">
                    No puedes realizar pedidos hasta que tu cuenta sea aprobada por un administrador.
                  </p>
                </div>
              ) : (
                <Button
                  data-testid="add-to-cart-btn"
                  onClick={addAllToCart}
                  disabled={!anyStockAvailable || units === 0}
                  className="w-full bg-black hover:bg-neutral-800 py-6 text-lg text-white font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  {units === 0 ? 'Añadir al carrito (introduce cantidades)' : `Añadir ${units} unidad${units === 1 ? '' : 'es'} al carrito`}
                </Button>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductDetail;
